import React, { useState, useEffect } from "react";

export type Option = { short: string; name: string };

type SearchSelectProps<T extends Option> = {
  width?: string;
  label: string;
  name: string; 
  value?: string; 
  onChange?: (name: string, value: string) => void; 
  dataUrl: string; 
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  placeholder?: string;
  extraClasses?: string;
  required?: boolean;
  maxResults?: number;
};

export default function SearchSelect<T extends Option>({
  width = "full",
  label,
  name,
  value,
  onChange,
  dataUrl,
  getOptionLabel = (o: T) => o.name,
  getOptionValue = (o: T) => o.short,
  placeholder = "Search...",
  extraClasses = "",
  required = false,
  maxResults = 10,
}: SearchSelectProps<T>) {
  const [options, setOptions] = useState<T[]>([]);
  const [search, setSearch] = useState<string>("");
  const [filtered, setFiltered] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  useEffect(() => {
    fetch(dataUrl)
      .then((res) => res.json())
      .then(setOptions)
      .catch(console.error);
  }, [dataUrl]);

  useEffect(() => {
    if (value && value !== selectedValue) {
      const selectedOption = options.find((o) => getOptionValue(o) === value);
      setSearch(selectedOption ? getOptionLabel(selectedOption) : "");
      setSelectedValue(value);
    }
  }, [value, options, getOptionLabel, getOptionValue, selectedValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const s = (search || "").toLowerCase();
      const results = options.filter(
        (o) =>
          getOptionLabel(o).toLowerCase().includes(s) ||
          getOptionValue(o).toLowerCase().includes(s)
      );
      setFiltered(results.slice(0, maxResults));
    }, 200);

    return () => clearTimeout(timeout);
  }, [search, options, getOptionLabel, getOptionValue, maxResults]);

  return (
    <div className="form-group relative">
      {label && <label htmlFor={name}>{label}</label>}
      <input
        id={name}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        placeholder={placeholder}
        required={required}
        className={`border px-2 rounded ${width ? `w-${width}` : "w-full"} ${extraClasses}`}
      />

      {isOpen && filtered.length > 0 && (
        <ul className="border rounded mt-1 max-h-48 overflow-y-auto bg-white z-50 absolute w-full">
          {filtered.map((o) => (
            <li
              key={getOptionValue(o)}
              onClick={() => {
                onChange?.(name, getOptionValue(o)); 
                setSearch(getOptionLabel(o)); 
                setSelectedValue(getOptionValue(o));
                setFiltered([]);
                setIsOpen(false);
              }}
              className="px-2 py-1 hover:bg-gray-200 cursor-pointer"
            >
              {getOptionLabel(o)} ({getOptionValue(o)})
            </li>
          ))}
        </ul>
      )}

      {isOpen && filtered.length === 0 && (
        <div className="px-2 py-1 text-gray-400">No results</div>
      )}
    </div>
  );
}